import type { AttachmentFileType } from "@inrev/common";
import type { SortingState } from "@tanstack/react-table";
import { memo } from "react";
import type { AttachmentContext } from "../../../analytics";
import type { UploadFile } from "../../../types";
import { cn } from "../../lib/utils";
import { Card, Text } from "../../primitives";
import { Dropzone } from "../../ui/Dropzone";
import {
	DynamicDataTable,
	DynamicDataTableColumn,
} from "../../ui/DynamicDataTable/DynamicDataTable";
import {
	DownloadAllAttachments,
	type DownloadConfig,
} from "../../uploaded-documents/DownloadAllAttachments";
import { PendingUploads } from "../../uploaded-documents/PendingUploads";
import type { UploadedDocumentRow } from "../../uploaded-documents/types";

type AttachmentsTableProps = {
	tableStorageKey: string;
	sectionName?: string;
	columns: DynamicDataTableColumn<UploadedDocumentRow>[];
	initialSorting: SortingState;
	isLoading: boolean;
	downloadConfig?: DownloadConfig;
	rows: UploadedDocumentRow[];
	allowUpload: boolean;
	handleUpload: (files: File[]) => void;
	filesPendingUpload: UploadFile<AttachmentFileType>[];
	filesCompletedUpload: UploadFile<AttachmentFileType>[];
	analyticsContext: AttachmentContext;
	useCardLayout?: boolean;
};

export const AttachmentsTable = memo(
	({
		columns,
		initialSorting,
		isLoading,
		downloadConfig,
		rows,
		tableStorageKey,
		sectionName = "Uploaded Documents",
		allowUpload,
		handleUpload,
		filesPendingUpload,
		filesCompletedUpload,
		analyticsContext,
		useCardLayout = false,
	}: AttachmentsTableProps) => {
		const showDownloadAll = downloadConfig && rows.length > 0;
		const downloadAll = showDownloadAll && (
			<DownloadAllAttachments
				downloadConfig={downloadConfig}
				allUploadedRows={rows}
				analyticsContext={analyticsContext}
			/>
		);

		const dropzone = allowUpload && (
			<Dropzone
				onSuccess={handleUpload}
				trackingId={`${analyticsContext}-attachment-dropzone`}
				className={cn(useCardLayout && "mb-10")}
			/>
		);

		const pendingUploads = (
			<PendingUploads
				pendingUploadFiles={filesPendingUpload}
				completedUploadFiles={filesCompletedUpload}
			/>
		);

		const table = (
			<DynamicDataTable
				name={tableStorageKey}
				columns={columns}
				data={isLoading ? undefined : rows}
				minHeight={400}
				maxHeight={600}
				initialSorting={initialSorting}
			/>
		);

		if (useCardLayout) {
			return (
				<Card
					elevation="raised"
					className="rounded-xl"
					id="attachments"
					data-testid="admin-bond-request-attachments-card"
				>
					<Card.Body className="p-6">
						<div className="flex flex-col w-full">
							<div className="flex items-center justify-between w-full mb-8">
								<Text type="title" color="primary">
									{sectionName}
								</Text>
								{downloadAll}
							</div>
							{dropzone}
							{pendingUploads}
							{table}
						</div>
					</Card.Body>
				</Card>
			);
		}

		return (
			<div id="attachments" className="w-full flex flex-col gap-4">
				<div className="flex flex-col gap-4 justify-center px-2">
					<div className="flex items-center">
						<Text type="title" color="primary" className="flex-1">
							{sectionName}
						</Text>
					</div>
					{dropzone}
				</div>
				{pendingUploads}
				{showDownloadAll && <div className="flex justify-end items-end">{downloadAll}</div>}
				{table}
			</div>
		);
	},
);
